import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { BranchModel } from 'src/app/models/management/branch.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CityTableModel } from 'src/app/models/tables/management/city-table.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CityService } from 'src/app/services/management/city.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { AdvancedOptionsBranchComponent } from '../advanced-options-branch/advanced-options-branch.component';

@Component({
  selector: 'app-register-branch',
  templateUrl: './register-branch.component.html',
  styleUrls: ['./register-branch.component.scss'],
  standalone: false
})
export class RegisterBranchComponent implements OnInit {

  @ViewChild('advancedOptions') advancedOptionsModalComponent?: CustomModalComponent | null = null;
  @ViewChild('selectCity') citySelectionModalComponent?: CustomModalComponent | null = null;
  ascendantCity: boolean = true;
  branchContract: BranchModel = new BranchModel();
  branchId: number = 0;
  city: string = '';
  cityData: PagedResponseModel<CityTableModel> = new PagedResponseModel<CityTableModel>();
  columnSortCity: string | null = null;
  componentAdvancedOptions = AdvancedOptionsBranchComponent;
  componentCitySelection = CustomTableSelectComponent;
  department: string = '';
  disabledForm: boolean = false;
  filtersCity: ColumnResponseModel[] = [];
  formBranch: FormGroup = new FormGroup({});
  headersCity: ColumnResponseModel[] = [];
  inputsForAdvancedOptionsModals$ = new BehaviorSubject<any>({
    enterpriseContract: 0,
    permissionUpdatable: false,
    disabledForm: false,
    listStore: []
  });
  inputsForSelectCityModal$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    filters: [],
    showCheckbox: true,
    showRecover: true
  });
  isOpened: string | undefined = undefined;
  limitCity: number = 10;
  listStore: any[] = [];
  nameWindow: string = 'Captura Sucursales';
  output$ = new Subject<any>();
  pageCity: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  url: string = 'branch';

  constructor(private confirmDialogService: ConfirmDialogService,
    private activatedRoute: ActivatedRoute,
    private branchService: BranchService,
    private cityService: CityService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private location: Location,
    private title: Title,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService) {
    this.validateForm(new BranchModel());
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getPermission();
    this.activatedRoute.params.subscribe(params => {
      this.branchId = Object.keys(params).length > 0 ? params?.id : null;
    });
    this.handleInitialState();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeCity(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeCity(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeCode(code: number) {
    if (!code) {
      this.resetContract();
    } else {
      await this.getBranchByCode(code);
    }
  }

  async createBranch(modelBranch: BranchModel) {
    try {
      const response = await firstValueFrom(this.branchService.createBranch(modelBranch));
      this.branchId = response.branchId;
      this.branchContract = response;
      this.validateForm(this.branchContract);
      this.location.replaceState(`/${this.url}/register/${this.branchId}`);
      this.toastHandlerService.showToast('success', `La sucursal fue creada exitosamente.`);
    } catch (error) {
      console.error('Error al crear la sucursal.', error);
    }
  }

  async createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear sucursales.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.resetContract();
  }

  async deleteBranch() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `El usuario no tiene permisos para eliminar sucursales.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (!this.branchId) {
      this.toastHandlerService.showToast('warning', 'Debe crear una sucursal.');
      return;
    }
    if (await this.confirmDialogService.confirm('¿Realmente desea eliminar la sucursal?')) {
      try {
        await firstValueFrom(this.branchService.deleteBranch(this.branchId));
        this.createNewRegister();
        this.toastHandlerService.showToast('success', 'La sucursal fue eliminada exitosamente.');
      } catch (error) {
        console.error('Error al eliminar la sucursal.', error);
      }
    }
  }

  async getButtonClick(buttonClick: any) {
    await this.handleBranchNavigation(buttonClick);
  }

  async getBranchByCode(code: number) {
    try {
      const response = await firstValueFrom(this.branchService.getBranchByCode(code));
      this.branchId = response.branchId;
      this.branchContract = response;
      this.validateForm(this.branchContract);
    } catch (error) {
      console.error('Error al obtener el sucursal por código.', error);
    }
  }

  async getBranchById() {
    try {
      const response = await firstValueFrom(this.branchService.getBranchById(this.branchId));
      this.branchContract = response;
      this.validateForm(this.branchContract);
    } catch (error) {
      console.error('Error al obtener el sucursal por id.', error);
    }
  }

  getBranchModel(): BranchModel {
    const model = this.formBranch.getRawValue() as BranchModel;
    const modelRecord = model as Record<string, any>;
    const fieldsToNullify = [
      'alterCode',
      'alterCode2',
      'logisticsEmail',
      'reteIvaCode'
    ];
    fieldsToNullify.forEach(field => {
      if (String(modelRecord[field]).trim() === '') {
        modelRecord[field] = null;
      }
    });
    return model;
  }

  async getCityDepartment(cityId: number) {
    try {
      const response: any = await firstValueFrom(this.cityService.getCityDepartment(cityId));
      this.city = response.city;
      this.department = response.department;
    } catch (error) {
      console.error('Error al obtener la ciudad y el departamento.', error);
    }
  }

  async getCitiesForSelection() {
    try {
      this.cityData = await firstValueFrom(this.cityService.getCitiesForSelection(this.pageCity, this.limitCity,
        this.columnSortCity, this.ascendantCity, this.filtersCity));
      this.updateInputsForSelectCityModal();
    } catch (error) {
      console.error('Error al obtener las ciudades para el modal.', error);
    }
  }

  async getColumnsForCitiesSelection() {
    try {
      if (this.headersCity.length === 0) {
        this.headersCity = await firstValueFrom(this.cityService.getColumnsForCitySelection());
      }
    } catch (error) {
      console.error('Error al buscar las columnas para el modal de ciudades.', error);
    }
  }

  async getPermission() {
    await this.getPermissionProfileByUrl();
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('error', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`);
      }
    } else {
      localStorage.clear();
    }
  }

  async getPermissionProfileByUrl() {
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
  }

  async getStores() {
    try {
      this.listStore = await firstValueFrom(this.branchService.getAllStoreForSelectAsync(this.branchId));
    } catch (error) {
      console.error('Error al obtener las bodegas.', error);
    }
  }

  async handleInitialState() {
    if (this.branchId) {
      await this.getBranchById();
    } else {
      this.createNewRegister();
    }
  }

  async handleBranchNavigation(buttonClick: any) {
    const code = this.formBranch.get("code")?.value;
    const actionMap: Record<string, () => Promise<BranchModel>> = {
      first: () =>
        firstValueFrom(this.branchService.getFirstBranchByKeysAsync()),
      previus: () =>
        firstValueFrom(this.branchService.getPreviusBranchByKeysAsync(code)),
      next: () =>
        firstValueFrom(this.branchService.getNextBranchByKeysAsync(code)),
      last: () =>
        firstValueFrom(this.branchService.getLastBranchByKeysAsync()),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.resetContract();
      return;
    }
    try {
      const branch = await action();
      this.branchId = branch.branchId;
      this.branchContract = branch;
      this.validateForm(this.branchContract);
      this.location.replaceState(`/${this.url}/register/${this.branchId}`);
    } catch (error) {
      console.error(`Error al buscar la sucursal (${buttonClick.target.id}).`, error);
    }
  }

  async handleCitySelection(branch: BranchModel) {
    if (branch?.cityId) {
      await this.getCityDepartment(branch.cityId);
    } else {
      this.city = '';
      this.department = '';
    }
  }

  async onFilterChangeCity(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersCity = this.removeDuplicates(filters);
    this.filtersCity.forEach((filter: any) => this.processFilter(filter));
    this.updateInputsForSelectCityModal();
  }

  async onPageChangeCity(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageCity = event.page;
    this.limitCity = event.pageSize;
    this.columnSortCity = event.sortField || null;
    this.ascendantCity = event.sortOrder === 1;
    await this.getCitiesForSelection();
  }

  async openCitySelectionModal() {
    try {
      this.filtersCity = [];
      this.updateInputsForSelectCityModal();
      await Promise.all([
        this.getColumnsForCitiesSelection(),
        this.getCitiesForSelection()
      ]);
      this.citySelectionModalComponent?.show();
    } catch (error) {
      console.error('Error al abrir el modal de selección de ciudad.', error);
    }
  }

  async openAdvancedOptions() {
    if (!this.branchId) {
      this.toastHandlerService.showToast('warning', 'Debe crear una sucursal.');
      return;
    }
    this.updateInputsForAdvancedOptionsModal();
    this.advancedOptionsModalComponent?.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersCity = this.filtersCity.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersCity.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async setCityDepartment(selectedItem: any) {
    const [city] = selectedItem;
    this.city = `${city.code} - ${city.name}`;
    this.department = `${city.departmentCode} - ${city.departmentName}`;
    this.formBranch.patchValue({ cityId: city.id, departmentId: city.departmentId });
  }

  async send() {
    this.validateFormControls();
    if (!this.formBranch.valid) {
      this.showValidationMessages();
      return;
    }
    const modelBranch = this.getBranchModel();
    if (this.branchId == 0) {
      await this.createBranch(modelBranch);
    } else {
      await this.updateBranch(modelBranch);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'El Código es obligatorio.',
        maxlength: 'El Código no puede tener más de 10 caracteres.'
      },
      alterCode: {
        maxlength: 'El Código Alterno no puede tener más de 10 caracteres.'
      },
      alterCode2: {
        maxlength: 'El Segundo Código Alterno no puede tener más de 10 caracteres.'
      },
      name: {
        required: 'El Nombre es obligatorio.',
        maxlength: 'El Nombre no puede tener más de 100 caracteres.'
      },
      address: {
        required: 'La Dirección es obligatoria.',
        maxlength: 'La Dirección no puede tener más de 70 caracteres.'
      },
      phone: {
        required: 'El Teléfono es obligatorio.',
        minlength: 'El Teléfono debe tener al menos 7 caracteres.',
        maxlength: 'El Teléfono no puede tener más de 15 caracteres.'
      },
      email: {
        required: 'El Correo Electrónico es obligatorio.',
        email: 'El formato del Correo Electrónico no es válido.',
        maxlength: 'El Correo Electrónico no puede tener más de 50 caracteres.'
      },
      departmentId: {
        required: 'El Departamento es obligatorio.',
        min: 'Debe seleccionar un Departamento válido.'
      },
      cityId: {
        required: 'La Ciudad es obligatoria.',
        min: 'Debe seleccionar una Ciudad válida.'
      },
      logisticsEmail: {
        required: 'El Correo Logístico es obligatorio.',
        email: 'El formato del Correo Logístico no es válido.',
        maxlength: 'El Correo Logístico no puede tener más de 50 caracteres.'
      },
      reteIvaCode: {
        maxlength: 'El Código de ReteIVA no puede tener más de 10 caracteres.'
      }
    };
    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formBranch.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  resetContract() {
    this.branchId = 0;
    this.listStore = [];
    this.city = '';
    this.department = '';
    this.branchContract = new BranchModel();
    this.validateForm(this.branchContract);
    this.location.replaceState(`/${this.url}/register`);
  }

  updateInputsForSelectCityModal() {
    this.inputsForSelectCityModal$.next({
      headers: this.headersCity,
      data: this.cityData,
      filters: this.filtersCity,
      showCheckbox: true,
      showRecover: true
    });
  }

  updateInputsForAdvancedOptionsModal() {
    this.inputsForAdvancedOptionsModals$.next({
      branchContract: this.branchContract,
      permissionUpdatable: this.permissionUpdatable,
      disabledForm: this.disabledForm,
      listStore: this.listStore
    });
  }

  updateFormPermision() {
    setTimeout(() => {
      if (this.permissionUpdatable) {
        this.disabledForm = false;
        this.formBranch.enable();
        const controlCode = this.formBranch.controls['code'];
        this.branchId !== 0 ? controlCode.enable() : controlCode.disable();
      } else {
        this.disabledForm = true;
        this.formBranch.disable();
      }
    });
  }

  async updateBranch(modelBranch: BranchModel) {
    this.branchContract.alterCode = modelBranch.alterCode;
    this.branchContract.alterCode2 = modelBranch.alterCode2;
    this.branchContract.name = modelBranch.name;
    this.branchContract.address = modelBranch.address;
    this.branchContract.phone = modelBranch.phone;
    this.branchContract.email = modelBranch.email;
    this.branchContract.logisticsEmail = modelBranch.logisticsEmail;
    this.branchContract.reteIvaCode = modelBranch.reteIvaCode;
    this.branchContract.departmentId = modelBranch.departmentId;
    this.branchContract.cityId = modelBranch.cityId;
    try {
      const response = await firstValueFrom(this.branchService.updateBranch(this.branchContract));
      this.branchContract = response;
      this.validateForm(this.branchContract);
      this.toastHandlerService.showToast('success', `La sucursal fue modificada exitosamente.`);
    } catch (error) {
      console.error('Error al actualizar la sucursal.', error);
    }
  }

  async validateForm(branch: BranchModel) {
    this.formBranch = this.formBuilder.group({
      code: [branch.code, [Validators.required, Validators.maxLength(10)]],
      alterCode: [branch.alterCode, [Validators.maxLength(10)]],
      alterCode2: [branch.alterCode2, [Validators.maxLength(10)]],
      name: [branch.name, [Validators.required, Validators.maxLength(100)]],
      address: [branch.address, [Validators.required, Validators.maxLength(70)]],
      phone: [branch.phone, [Validators.required, Validators.minLength(7), Validators.maxLength(15)]],
      email: [branch.email, [Validators.required, Validators.email, Validators.maxLength(50)]],
      departmentId: [branch.departmentId, [Validators.required, Validators.min(1)]],
      cityId: [branch.cityId, [Validators.required, Validators.min(1)]],
      logisticsEmail: [branch.logisticsEmail, [Validators.email, Validators.required, Validators.maxLength(50)]],
      reteIvaCode: [branch.reteIvaCode, [Validators.maxLength(10)]]
    });
    this.updateFormPermision();
    if (branch.branchId) {
      await Promise.all([
        this.getStores(),
        this.handleCitySelection(branch)
      ]);
    }
  }

  validateFormControls() {
    Object.keys(this.formBranch.controls).forEach((key) => {
      const control = this.formBranch.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
